import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { Search, Filter, Book } from 'lucide-react';
import { FaLock } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import KpiTile from '../components/finance/KpiTile';
import { getAllCourses } from '../services/firebase/courses';

/**
 * Locked preview of the portal Courses page (/portal/courses) for /start
 * prospects. Real published courses are listed so the catalogue is honest, but
 * every card is locked and the search/filter controls are inert.
 */

const getCategoryColor = (category) => {
  switch (category) {
    case 'Sales':
      return 'bg-red-600';
    case 'Operations':
      return 'bg-blue-600';
    case 'Technology':
      return 'bg-purple-600';
    case 'Finance':
      return 'bg-green-600';
    case 'Marketing':
      return 'bg-orange-500';
    case 'HR':
      return 'bg-yellow-500';
    default:
      return 'bg-gray-600';
  }
};

const sectionCardClass =
  'rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-6';

const LockedCourseCard = ({ title, description, thumbnailUrl, category }) => {
  const { darkMode } = useTheme();

  return (
    <div className="rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm p-5 flex flex-col h-full relative overflow-hidden">
      <div
        className={`absolute top-4 left-4 px-2.5 py-0.5 rounded-full text-[10px] font-medium text-white z-10 ${getCategoryColor(
          category
        )}`}
      >
        {category}
      </div>

      {thumbnailUrl ? (
        <div className="mb-4 mt-6">
          <img src={thumbnailUrl} alt={title} className="w-full h-40 object-cover rounded-lg" />
        </div>
      ) : (
        <div className="mb-4 mt-6 flex justify-center">
          <div className="w-full h-32 rounded-lg bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center">
            <Book className="w-10 h-10 text-red-600 opacity-60" />
          </div>
        </div>
      )}

      <div className="mb-3">
        <h3
          className={`font-semibold text-lg mb-1 line-clamp-2 ${
            darkMode ? 'text-white' : 'text-gray-800'
          }`}
        >
          {title}
        </h3>
        <p className={`text-sm line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {description}
        </p>
      </div>

      <div className="mt-auto">
        <div
          className={`w-full rounded-full h-1.5 overflow-hidden mb-1.5 ${
            darkMode ? 'bg-gray-700' : 'bg-gray-200'
          }`}
        >
          <div className="h-1.5 rounded-full w-0" />
        </div>
        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          0% complete
        </span>
      </div>

      <div className="absolute inset-0 bg-gray-900/10 dark:bg-gray-900/40 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-20">
        <FaLock className="text-3xl text-gray-500 dark:text-gray-300" aria-hidden="true" />
      </div>
    </div>
  );
};

const StartCourses = () => {
  const { darkMode } = useTheme();
  const { isMobile } = useResponsive();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const departments = ['All', 'Sales', 'Operations', 'Marketing', 'Finance', 'Technology', 'HR'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const fetchedCourses = await getAllCourses();
        setCourses(fetchedCourses.filter((course) => course.isPublished));
        setError(null);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (error) {
    return (
      <div className={`p-6 md:p-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Book size={48} className="mx-auto text-red-400 mb-4" />
            <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Error loading courses
            </h3>
            <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 md:p-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      <div className="text-center mb-6">
        <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Courses
        </h1>
      </div>

      <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-3 mb-6`}>
        <KpiTile
          label="Total"
          value={String(courses.length)}
          showSourceBadge={false}
          icon={<span className="text-base leading-none">🎓</span>}
          loading={loading}
        />
        <KpiTile
          label="In Progress"
          value="—"
          showSourceBadge={false}
          icon={<FaLock className="text-[10px]" />}
          loading={loading}
        />
        <KpiTile
          label="Completed"
          value="—"
          showSourceBadge={false}
          icon={<FaLock className="text-[10px]" />}
          loading={loading}
        />
        <KpiTile
          label="New Courses & Lessons"
          value="—"
          showSourceBadge={false}
          icon={<FaLock className="text-[10px]" />}
          loading={loading}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 mb-6">
          <BBALoader size={40} />
        </div>
      ) : (
        <>
          <div className={`${sectionCardClass} !py-4`}>
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="flex-1 relative">
                <Search
                  className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                />
                <input
                  type="text"
                  placeholder="Search courses..."
                  disabled
                  className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm cursor-not-allowed ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none`}
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter
                  className={`h-4 w-4 shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                />
                <select
                  disabled
                  className={`px-3 py-2.5 rounded-lg border text-sm min-w-[140px] cursor-not-allowed ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none`}
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {courses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {courses.map((course) => (
                <LockedCourseCard
                  key={course.id}
                  title={course.title}
                  description={course.description}
                  thumbnailUrl={course.thumbnailUrl}
                  category={course.category || 'General'}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Book
                size={64}
                className={`mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}
              />
              <h3
                className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}
              >
                No courses available
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                There are no published courses at this time.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StartCourses;
